import { Injectable } from '@angular/core';
import { DocumentNode, FetchResult } from '@apollo/client/core';
import {
    DigitaiChartType,
    DigitaiCreateFleetTab,
    DigitaiFleetTab,
    DigitaiUpdateFleetTabById,
    DigitaiUpdateFleetTabs,
} from '@apps/digitai';
import { ChartTypeService } from '@apps/digitai/chart-type';
import {
    createMutation,
    deleteByIdMutation,
    deleteMutation,
    fields,
    findByIdQuery,
    findByIdWithRelationsQuery,
    findQuery,
    getQuery,
    insertMutation,
    paginationQuery,
    updateByIdMutation,
    updateMutation,
} from '@apps/digitai/fleet-tab';
import {
    GraphQLHeaders,
    GraphQLService,
    GridData,
    parseGqlFields,
    QueryStatement,
} from '@aurora';
import { BehaviorSubject, first, map, Observable, tap } from 'rxjs';
import { getRelations } from './fleet-tab.graphql';

@Injectable({
    providedIn: 'root',
})
export class FleetTabService {
    paginationSubject$: BehaviorSubject<GridData<DigitaiFleetTab> | null> =
        new BehaviorSubject(null);
    fleetTabSubject$: BehaviorSubject<DigitaiFleetTab | null> =
        new BehaviorSubject(null);
    fleetTabsSubject$: BehaviorSubject<DigitaiFleetTab[] | null> =
        new BehaviorSubject(null);

    // scoped subjects
    paginationScoped: {
        [key: string]: BehaviorSubject<GridData<DigitaiFleetTab> | null>;
    } = {};
    fleetTabScoped: { [key: string]: BehaviorSubject<DigitaiFleetTab | null> } =
        {};
    fleetTabsScoped: {
        [key: string]: BehaviorSubject<DigitaiFleetTab[] | null>;
    } = {};

    constructor(
        private readonly chartTypeService: ChartTypeService,
        private readonly graphqlService: GraphQLService
    ) {}

    /**
     * Getters
     */
    get pagination$(): Observable<GridData<DigitaiFleetTab>> {
        return this.paginationSubject$.asObservable();
    }

    get fleetTab$(): Observable<DigitaiFleetTab> {
        return this.fleetTabSubject$.asObservable();
    }

    get fleetTabs$(): Observable<DigitaiFleetTab[]> {
        return this.fleetTabsSubject$.asObservable();
    }

    // allows to store different types of pagination under different scopes this allows us
    // to have multiple observables with different streams of pagination data.
    setScopePagination(
        scope: string,
        pagination: GridData<DigitaiFleetTab>
    ): void {
        if (this.paginationScoped[scope]) {
            this.paginationScoped[scope].next(pagination);
            return;
        }
        // create new subject if not exist
        this.paginationScoped[scope] = new BehaviorSubject(pagination);
    }

    // get pagination observable by scope
    getScopePagination(scope: string): Observable<GridData<DigitaiFleetTab>> {
        if (!this.paginationScoped[scope])
            this.paginationScoped[scope] = new BehaviorSubject(null);
        return this.paginationScoped[scope].asObservable();
    }

    setScopeFleetTab(scope: string, object: DigitaiFleetTab): void {
        if (this.fleetTabScoped[scope]) {
            this.fleetTabScoped[scope].next(object);
            return;
        }
        // create new subject if not exist
        this.fleetTabScoped[scope] = new BehaviorSubject(object);
    }

    getScopeFleetTab(scope: string): Observable<DigitaiFleetTab> {
        if (!this.fleetTabScoped[scope])
            this.fleetTabScoped[scope] = new BehaviorSubject(null);
        return this.fleetTabScoped[scope].asObservable();
    }

    setScopeFleetTabs(scope: string, objects: DigitaiFleetTab[]): void {
        if (this.fleetTabsScoped[scope]) {
            this.fleetTabsScoped[scope].next(objects);
            return;
        }
        // create new subject if not exist
        this.fleetTabsScoped[scope] = new BehaviorSubject(objects);
    }

    getScopeFleetTabs(scope: string): Observable<DigitaiFleetTab[]> {
        if (!this.fleetTabsScoped[scope])
            this.fleetTabsScoped[scope] = new BehaviorSubject(null);
        return this.fleetTabsScoped[scope].asObservable();
    }

    pagination({
        graphqlStatement = paginationQuery,
        query = {},
        constraint = {},
        headers = {},
        scope,
    }: {
        graphqlStatement?: DocumentNode;
        query?: QueryStatement;
        constraint?: QueryStatement;
        headers?: GraphQLHeaders;
        scope?: string;
    } = {}): Observable<GridData<DigitaiFleetTab>> {
        // get result, map ang throw data across observable
        return this.graphqlService
            .client()
            .watchQuery<{ pagination: GridData<DigitaiFleetTab> }>({
                query: graphqlStatement,
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges.pipe(
                first(),
                map((result) => result.data.pagination),
                tap((pagination) =>
                    scope
                        ? this.setScopePagination(scope, pagination)
                        : this.paginationSubject$.next(pagination)
                )
            );
    }

    findById({
        graphqlStatement = findByIdQuery,
        id = null,
        constraint = {},
        headers = {},
        scope,
    }: {
        graphqlStatement?: DocumentNode;
        id?: string;
        constraint?: QueryStatement;
        headers?: GraphQLHeaders;
        scope?: string;
    } = {}): Observable<{
        object: DigitaiFleetTab;
    }> {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiFleetTab;
            }>({
                query: parseGqlFields(graphqlStatement, fields, constraint),
                variables: {
                    id,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges.pipe(
                first(),
                map((result) => result.data),
                tap((data) =>
                    scope
                        ? this.setScopeFleetTab(scope, data.object)
                        : this.fleetTabSubject$.next(data.object)
                )
            );
    }

    findByIdWithRelations({
        graphqlStatement = findByIdWithRelationsQuery,
        id = '',
        constraint = {},
        queryChartTypes = {},
        constraintChartTypes = {},
        headers = {},
        scope,
    }: {
        graphqlStatement?: DocumentNode;
        id?: string;
        constraint?: QueryStatement;
        queryChartTypes?: QueryStatement;
        constraintChartTypes?: QueryStatement;
        headers?: GraphQLHeaders;
        scope?: string;
    } = {}): Observable<{
        object: DigitaiFleetTab;
        digitaiGetChartTypes: DigitaiChartType[];
    }> {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiFleetTab;
                digitaiGetChartTypes: DigitaiChartType[];
            }>({
                query: parseGqlFields(graphqlStatement, fields, constraint),
                variables: {
                    id,
                    constraint,
                    queryChartTypes,
                    constraintChartTypes,
                },
                context: {
                    headers,
                },
            })
            .valueChanges.pipe(
                first(),
                map((result) => result.data),
                tap((data) => {
                    if (scope) {
                        this.setScopeFleetTab(scope, data.object);
                    } else {
                        this.fleetTabSubject$.next(data.object);
                    }
                    this.chartTypeService.chartTypesSubject$.next(
                        data.digitaiGetChartTypes
                    );
                })
            );
    }

    find({
        graphqlStatement = findQuery,
        query = {},
        constraint = {},
        headers = {},
        scope,
    }: {
        graphqlStatement?: DocumentNode;
        query?: QueryStatement;
        constraint?: QueryStatement;
        headers?: GraphQLHeaders;
        scope?: string;
    } = {}): Observable<{
        object: DigitaiFleetTab;
    }> {
        return this.graphqlService
            .client()
            .watchQuery<{
                object: DigitaiFleetTab;
            }>({
                query: parseGqlFields(
                    graphqlStatement,
                    fields,
                    query,
                    constraint
                ),
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges.pipe(
                first(),
                map((result) => result.data),
                tap((data) =>
                    scope
                        ? this.setScopeFleetTab(scope, data.object)
                        : this.fleetTabSubject$.next(data.object)
                )
            );
    }

    get({
        graphqlStatement = getQuery,
        query = {},
        constraint = {},
        headers = {},
        scope,
    }: {
        graphqlStatement?: DocumentNode;
        query?: QueryStatement;
        constraint?: QueryStatement;
        headers?: GraphQLHeaders;
        scope?: string;
    } = {}): Observable<{
        objects: DigitaiFleetTab[];
    }> {
        return this.graphqlService
            .client()
            .watchQuery<{
                objects: DigitaiFleetTab[];
            }>({
                query: parseGqlFields(
                    graphqlStatement,
                    fields,
                    query,
                    constraint
                ),
                variables: {
                    query,
                    constraint,
                },
                context: {
                    headers,
                },
            })
            .valueChanges.pipe(
                first(),
                map((result) => result.data),
                tap((data) =>
                    scope
                        ? this.setScopeFleetTabs(scope, data.objects)
                        : this.fleetTabsSubject$.next(data.objects)
                )
            );
    }

    getRelations({
        queryChartTypes = {},
        constraintChartTypes = {},
        headers = {},
    }: {
        queryChartTypes?: QueryStatement;
        constraintChartTypes?: QueryStatement;
        headers?: GraphQLHeaders;
    } = {}): Observable<{
        digitaiGetChartTypes: DigitaiChartType[];
    }> {
        return this.graphqlService
            .client()
            .watchQuery<{
                digitaiGetChartTypes: DigitaiChartType[];
            }>({
                query: getRelations,
                variables: {
                    queryChartTypes,
                    constraintChartTypes,
                },
                context: {
                    headers,
                },
            })
            .valueChanges.pipe(
                first(),
                map((result) => result.data),
                tap((data) => {
                    this.chartTypeService.chartTypesSubject$.next(
                        data.digitaiGetChartTypes
                    );
                })
            );
    }

    create<T>({
        graphqlStatement = createMutation,
        object = null,
        headers = {},
    }: {
        graphqlStatement?: DocumentNode;
        object?: DigitaiCreateFleetTab;
        headers?: GraphQLHeaders;
    } = {}): Observable<FetchResult<T>> {
        return this.graphqlService.client().mutate({
            mutation: graphqlStatement,
            variables: {
                payload: object,
            },
            context: {
                headers,
            },
        });
    }

    insert<T>({
        graphqlStatement = insertMutation,
        objects = null,
        headers = {},
    }: {
        graphqlStatement?: DocumentNode;
        objects?: DigitaiCreateFleetTab[];
        headers?: GraphQLHeaders;
    } = {}): Observable<FetchResult<T>> {
        return this.graphqlService.client().mutate({
            mutation: graphqlStatement,
            variables: {
                payload: objects,
            },
            context: {
                headers,
            },
        });
    }

    updateById<T>({
        graphqlStatement = updateByIdMutation,
        object = null,
        headers = {},
    }: {
        graphqlStatement?: DocumentNode;
        object?: DigitaiUpdateFleetTabById;
        headers?: GraphQLHeaders;
    } = {}): Observable<FetchResult<T>> {
        return this.graphqlService.client().mutate({
            mutation: graphqlStatement,
            variables: {
                payload: object,
            },
            context: {
                headers,
            },
        });
    }

    update<T>({
        graphqlStatement = updateMutation,
        object = null,
        query = {},
        constraint = {},
        headers = {},
    }: {
        graphqlStatement?: DocumentNode;
        object?: DigitaiUpdateFleetTabs;
        query?: QueryStatement;
        constraint?: QueryStatement;
        headers?: GraphQLHeaders;
    } = {}): Observable<FetchResult<T>> {
        return this.graphqlService.client().mutate({
            mutation: graphqlStatement,
            variables: {
                payload: object,
                query,
                constraint,
            },
            context: {
                headers,
            },
        });
    }

    deleteById<T>({
        graphqlStatement = deleteByIdMutation,
        id = null,
        constraint = {},
        headers = {},
    }: {
        graphqlStatement?: DocumentNode;
        id?: string;
        constraint?: QueryStatement;
        headers?: GraphQLHeaders;
    } = {}): Observable<FetchResult<T>> {
        return this.graphqlService.client().mutate({
            mutation: graphqlStatement,
            variables: {
                id,
                constraint,
            },
            context: {
                headers,
            },
        });
    }

    delete<T>({
        graphqlStatement = deleteMutation,
        query = {},
        constraint = {},
        headers = {},
    }: {
        graphqlStatement?: DocumentNode;
        query?: QueryStatement;
        constraint?: QueryStatement;
        headers?: GraphQLHeaders;
    } = {}): Observable<FetchResult<T>> {
        return this.graphqlService.client().mutate({
            mutation: graphqlStatement,
            variables: {
                query,
                constraint,
            },
            context: {
                headers,
            },
        });
    }
}
